import { createApp, h, provide } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from '@/router'
import { ApolloClients } from '@vue/apollo-composable'
import { apolloClient, routerApolloClient } from '@/shared/graphql/client'
import VueVirtualScroller from 'vue-virtual-scroller'
import TruncateDirective from '@dacr/vue-truncate'
import { createHead } from '@unhead/vue'
import { analytics } from '@/shared/services/analytics'
import { initConsent } from '@dacr/consent'
import { useFeatureFlagStore } from '@/shared/stores/featureFlags'
import { i18n } from '@/shared/i18n'
import { refreshServerMessages } from '@/shared/i18n/serverMessages'

import '@/shared/dialogs/registrations'

import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import '@/assets/css/font.css'
import '@/assets/css/tailwind.css'
import '@/assets/css/transition.css'

const app = createApp({
  setup() {
    provide(ApolloClients, {
      default: apolloClient,
      router: routerApolloClient,
    })
  },
  render: () => h(App),
})

// Native webviews get no web trackers and no cookie banner (§6.5) — consent is
// the native app's responsibility and the cookie jar is not assumed shared. Same
// predicate as webview store `isActive`, checked here before Pinia exists.
const isWebview =
  !!(
    (window.webkit && !navigator.userAgent.toLowerCase().includes('safari')) ||
    window.Android
  ) && navigator.userAgent.toLowerCase().includes('dacr')

if (!isWebview) {
  // Resolve consent context early, then wire the consent-gated tracker facade to
  // it (§2.6/§3.5). PostHog loads cookieless immediately (pending) and switches
  // to full on an analytics grant; GA/Meta/LogRocket stay off until their grant.
  initConsent({
    apiUrl: import.meta.env.VITE_GRAPHQL_ROUTER_API,
    buildSha: import.meta.env.VITE_BUILD_SHA,
  })
  analytics.init()
}

// Not awaited — the bundled snapshot already renders correctly, this only picks
// up copy published since the last frontend deploy.
refreshServerMessages()

app.use(createPinia())

const featureFlagStore = useFeatureFlagStore()
featureFlagStore.init()

app.use(router)
app.use(i18n)
app.use(VueVirtualScroller)
app.use(createHead())

app.directive('truncate', TruncateDirective)

app.mount('#app')
